import React, { useState, useEffect } from 'react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { TypingAnimation } from './TypingAnimation';
import ReactMarkdown from 'react-markdown';

interface ChatBubbleProps {
  message: string;
  isTyping?: boolean;
  onTypingComplete?: () => void;
  showAvatar?: boolean;
}

export function ChatBubble({ message, isTyping = false, onTypingComplete, showAvatar = true }: ChatBubbleProps) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  const avatarUrl = `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/image_library/avatars/Lexi%20micro%20gif.gif`;
  
  // Preload the image to minimize fallback display time
  useEffect(() => {
    let isMounted = true;
    const img = new Image();
    img.onload = () => isMounted && setImageLoaded(true);
    img.onerror = () => isMounted && setImageError(true);
    img.src = avatarUrl;
    return () => { isMounted = false; };
  }, [avatarUrl]);

  return (
    <div className="flex items-start gap-3 mb-6">
      {showAvatar && (
        <Avatar className="flex-shrink-0" style={{ width: '100px', height: '100px' }}>
          {(imageLoaded || imageError) && (
            <AvatarImage 
              src={avatarUrl}
              alt="Lexi AI" 
              className="object-cover"
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageError(true)}
            />
          )}
          <AvatarFallback className="bg-[#ecb22d] text-white text-xl font-medium">
            L
          </AvatarFallback>
        </Avatar>
      )}
      <div 
        className="bg-white p-4 rounded-2xl border border-gray-100 max-w-md"
        style={{ boxShadow: "1px 1px 3px rgba(0, 0, 0, 0.08)" }}
      >
        {isTyping ? (
          <TypingAnimation 
            text={message} 
            speed={15}
            onComplete={onTypingComplete}
            className="text-gray-800 text-base whitespace-pre-wrap"
          />
        ) : (
          <div className="text-gray-800 text-base prose prose-sm max-w-none prose-headings:text-gray-800 prose-a:text-blue-600 prose-p:text-gray-800 prose-strong:text-gray-800">
            <ReactMarkdown
              components={{
                a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" />,
                p: ({ node, ...props }) => <p className="mb-2 last:mb-0 text-gray-800" {...props} />,
                strong: ({ node, ...props }) => <strong className="font-bold text-gray-800" {...props} />,
                br: ({ node, ...props }) => <br {...props} />,
              }}
            >
              {message}
            </ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  );
} 